<template>
  <div
    class="pay-range-scale"
    :class="[
      `pay-range-scale--${orientation}`,
      `pay-range-scale--${state}`,
      { 'pay-range-scale--disabled': isDisabled },
    ]"
    :aria-disabled="isDisabled ? 'true' : undefined"
  >
    <p class="pay-range-scale__heading">{{ title }}</p>

    <div
      v-if="orientation === PAY_RANGE_SCALE_ORIENTATION.horizontal"
      class="pay-range-scale__steps pay-range-scale__steps--horizontal"
    >
      <div class="pay-range-scale__step">
        <div class="pay-range-scale__marker-track">
          <div class="pay-range-scale__bound-icon">
            <Icon name="pay_range_left_icon" />
          </div>
          <div
            class="pay-range-scale__connector pay-range-scale__connector--horizontal pay-range-scale__connector--red"
          />
        </div>
        <div class="pay-range-scale__step-body">
          <p class="pay-range-scale__step-title">{{ minTitle }}</p>
          <slot name="min-input">
            <div class="pay-range-scale__input" :class="inputSurfaceClass">
              <div class="pay-range-scale__input-value">
                {{ minAmountDisplay }}
              </div>
              <div class="pay-range-scale__input-unit">{{ amountUnit }}</div>
            </div>
          </slot>
          <p
            v-if="showMinError"
            class="pay-range-scale__error"
          >
            {{ minErrorMessage }}
          </p>
          <p class="pay-range-scale__supporting pay-range-scale__supporting--min">
            {{ minSupportingText }}
          </p>
        </div>
      </div>

      <div class="pay-range-scale__step">
        <div class="pay-range-scale__marker-track">
          <div
            class="pay-range-scale__step-icon pay-range-scale__step-icon--red"
            aria-hidden="true"
          >
            <span class="pay-range-scale__step-icon-dot" />
          </div>
          <div
            class="pay-range-scale__connector pay-range-scale__connector--horizontal pay-range-scale__connector--green"
          />
        </div>
        <div class="pay-range-scale__step-body">
          <p class="pay-range-scale__step-title">{{ readiness50Title }}</p>
          <p class="pay-range-scale__supporting pay-range-scale__supporting--wrap">
            {{ readiness50SupportingText }}
          </p>
        </div>
      </div>

      <div class="pay-range-scale__step">
        <div class="pay-range-scale__marker-track">
          <div
            class="pay-range-scale__step-icon pay-range-scale__step-icon--green"
            aria-hidden="true"
          >
            <span class="pay-range-scale__step-icon-dot" />
          </div>
          <div
            class="pay-range-scale__connector pay-range-scale__connector--horizontal pay-range-scale__connector--blue"
          />
        </div>
        <div class="pay-range-scale__step-body">
          <p class="pay-range-scale__step-title">{{ readiness100Title }}</p>
          <p class="pay-range-scale__supporting pay-range-scale__supporting--wrap">
            {{ readiness100SupportingText }}
          </p>
        </div>
      </div>

      <div class="pay-range-scale__step">
        <div class="pay-range-scale__marker-track">
          <div
            class="pay-range-scale__step-icon pay-range-scale__step-icon--blue"
            aria-hidden="true"
          >
            <span class="pay-range-scale__step-icon-dot" />
          </div>
          <div class="pay-range-scale__bound-icon">
            <Icon name="pay_range_right_icon" />
          </div>
        </div>
        <div class="pay-range-scale__step-body">
          <p class="pay-range-scale__step-title">{{ maxTitle }}</p>
          <slot name="max-input">
            <div class="pay-range-scale__input" :class="inputSurfaceClass">
              <div class="pay-range-scale__input-value">
                {{ maxAmountDisplay }}
              </div>
              <div class="pay-range-scale__input-unit">{{ amountUnit }}</div>
            </div>
          </slot>
          <p
            v-if="showMaxError"
            class="pay-range-scale__error"
          >
            {{ maxErrorMessage }}
          </p>
          <p class="pay-range-scale__supporting pay-range-scale__supporting--min">
            {{ maxSupportingText }}
          </p>
        </div>
      </div>
    </div>

    <div v-else class="pay-range-scale__steps pay-range-scale__steps--vertical">
      <div class="pay-range-scale__vertical-row">
        <div class="pay-range-scale__connector-wrap">
          <Icon name="pay_range_top_icon" class="pay-range-scale__bound-icon-svg" />
          <div
            class="pay-range-scale__step-icon pay-range-scale__step-icon--blue"
            aria-hidden="true"
          >
            <span class="pay-range-scale__step-icon-dot" />
          </div>
          <div
            class="pay-range-scale__connector pay-range-scale__connector--vertical pay-range-scale__connector--blue pay-range-scale__connector--grow"
          />
        </div>
        <div class="pay-range-scale__vertical-content pay-range-scale__vertical-content--max">
          <p class="pay-range-scale__step-title">{{ maxTitle }}</p>
          <slot name="max-input">
            <div class="pay-range-scale__input" :class="inputSurfaceClass">
              <div class="pay-range-scale__input-value">
                {{ maxAmountDisplay }}
              </div>
              <div class="pay-range-scale__input-unit">{{ amountUnit }}</div>
            </div>
          </slot>
          <p
            v-if="showMaxError"
            class="pay-range-scale__error pay-range-scale__error--vertical"
          >
            {{ maxErrorMessage }}
          </p>
          <p class="pay-range-scale__supporting pay-range-scale__supporting--vertical">
            {{ maxSupportingText }}
          </p>
        </div>
      </div>

      <div class="pay-range-scale__vertical-row">
        <div class="pay-range-scale__connector-wrap">
          <div
            class="pay-range-scale__step-icon pay-range-scale__step-icon--green"
            aria-hidden="true"
          >
            <span class="pay-range-scale__step-icon-dot" />
          </div>
          <div
            class="pay-range-scale__connector pay-range-scale__connector--vertical pay-range-scale__connector--green pay-range-scale__connector--grow"
          />
        </div>
        <div class="pay-range-scale__vertical-content pay-range-scale__vertical-content--milestone">
          <p class="pay-range-scale__step-title">{{ readiness100Title }}</p>
          <p class="pay-range-scale__supporting">
            {{ readiness100SupportingText }}
          </p>
        </div>
      </div>

      <div class="pay-range-scale__vertical-row pay-range-scale__vertical-row--min-block">
        <div class="pay-range-scale__connector-wrap">
          <div
            class="pay-range-scale__step-icon pay-range-scale__step-icon--red"
            aria-hidden="true"
          >
            <span class="pay-range-scale__step-icon-dot" />
          </div>
          <div
            class="pay-range-scale__connector pay-range-scale__connector--vertical pay-range-scale__connector--red pay-range-scale__connector--short"
          />
          <Icon name="pay_range_bottom_icon" class="pay-range-scale__bound-icon-svg" />
        </div>
        <div class="pay-range-scale__vertical-content pay-range-scale__vertical-content--min-group">
          <div class="pay-range-scale__milestone-block">
            <p class="pay-range-scale__step-title">{{ readiness50Title }}</p>
            <p class="pay-range-scale__supporting">
              {{ readiness50SupportingText }}
            </p>
          </div>
          <div class="pay-range-scale__min-block">
            <p class="pay-range-scale__step-title">{{ minTitle }}</p>
            <slot name="min-input">
              <div class="pay-range-scale__input" :class="inputSurfaceClass">
                <div class="pay-range-scale__input-value">
                  {{ minAmountDisplay }}
                </div>
                <div class="pay-range-scale__input-unit">{{ amountUnit }}</div>
              </div>
            </slot>
            <p
              v-if="showMinError"
              class="pay-range-scale__error pay-range-scale__error--vertical"
            >
              {{ minErrorMessage }}
            </p>
            <p class="pay-range-scale__supporting pay-range-scale__supporting--vertical">
              {{ minSupportingText }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import Icon from "@components/icons/Icon.vue";
import {
  PAY_RANGE_SCALE_ORIENTATION,
  PAY_RANGE_SCALE_ORIENTATIONS,
  PAY_RANGE_SCALE_STATE,
  PAY_RANGE_SCALE_STATES,
} from "./payRangeScaleConstants";

const props = defineProps({
  orientation: {
    type: String,
    default: PAY_RANGE_SCALE_ORIENTATION.horizontal,
    validator: (value) => PAY_RANGE_SCALE_ORIENTATIONS.includes(value),
  },
  state: {
    type: String,
    default: PAY_RANGE_SCALE_STATE.default,
    validator: (value) => PAY_RANGE_SCALE_STATES.includes(value),
  },
  title: {
    type: String,
    default: "Amounts at 1 FTE*",
  },
  minTitle: {
    type: String,
    default: "Bottom of the Range (Min)",
  },
  maxTitle: {
    type: String,
    default: "Top of the Range (Max)",
  },
  minAmount: {
    type: String,
    default: "",
  },
  maxAmount: {
    type: String,
    default: "",
  },
  amountPlaceholder: {
    type: String,
    default: "Enter amount",
  },
  amountUnit: {
    type: String,
    default: "USD/Year",
  },
  minSupportingText: {
    type: String,
    default: "",
  },
  maxSupportingText: {
    type: String,
    default: "",
  },
  readiness50Title: {
    type: String,
    default: "",
  },
  readiness50SupportingText: {
    type: String,
    default: "",
  },
  readiness100Title: {
    type: String,
    default: "",
  },
  readiness100SupportingText: {
    type: String,
    default: "",
  },
  minErrorMessage: {
    type: String,
    default: "",
  },
  maxErrorMessage: {
    type: String,
    default: "",
  },
});

const isDisabled = computed(() => props.state === PAY_RANGE_SCALE_STATE.disabled);
const isEmpty = computed(() => props.state === PAY_RANGE_SCALE_STATE.empty);
const isError = computed(() => props.state === PAY_RANGE_SCALE_STATE.error);

const showMinError = computed(
  () => isError.value && Boolean(props.minErrorMessage)
);
const showMaxError = computed(
  () => isError.value && Boolean(props.maxErrorMessage)
);

const minAmountDisplay = computed(() => {
  if (props.minAmount) return props.minAmount;
  if (isEmpty.value) return props.amountPlaceholder;
  return props.minAmount;
});

const maxAmountDisplay = computed(() => {
  if (props.maxAmount) return props.maxAmount;
  if (isEmpty.value) return props.amountPlaceholder;
  return props.maxAmount;
});

const inputSurfaceClass = computed(() => {
  switch (props.state) {
    case PAY_RANGE_SCALE_STATE.focused:
      return "pay-range-scale__input--focused";
    case PAY_RANGE_SCALE_STATE.changed:
      return "pay-range-scale__input--changed";
    case PAY_RANGE_SCALE_STATE.error:
      return "pay-range-scale__input--error";
    case PAY_RANGE_SCALE_STATE.disabled:
      return "pay-range-scale__input--disabled";
    default:
      return "pay-range-scale__input--default";
  }
});
</script>

<style scoped>
.pay-range-scale {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  box-sizing: border-box;
  font-family: Inter, sans-serif;
}

.pay-range-scale--horizontal {
  padding: var(--spacing-md, 8px) var(--spacing-xl, 16px);
  width: 100%;
  max-width: 1040px;
}

.pay-range-scale--vertical {
  padding: 12px 37px;
  width: 100%;
  max-width: 545px;
}

.pay-range-scale__heading {
  margin: 0 0 var(--spacing-md, 8px);
  color: var(--headings-or-titles, #344054);
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  white-space: nowrap;
}

.pay-range-scale__steps--horizontal {
  display: flex;
  align-items: flex-start;
  width: 100%;
}

.pay-range-scale__step {
  display: flex;
  flex: 1 0 0;
  flex-direction: column;
  gap: var(--spacing-md, 8px);
  min-width: 0;
}

.pay-range-scale__marker-track {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs, 4px);
  padding-right: 4px;
  width: 100%;
}

.pay-range-scale__step:last-child .pay-range-scale__marker-track {
  padding-right: 0;
}

.pay-range-scale__bound-icon {
  display: flex;
  flex-shrink: 0;
  padding: 4px;
}

.pay-range-scale__bound-icon-svg {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
}

.pay-range-scale__connector--horizontal {
  flex: 1 0 0;
  height: 2px;
  min-width: 0;
  border-radius: 1px;
}

.pay-range-scale__connector--vertical {
  width: 2px;
  border-radius: var(--spacing-xxs, 2px);
}

.pay-range-scale__connector--grow {
  flex: 1 0 0;
  min-height: 0;
}

.pay-range-scale__connector--short {
  height: 62px;
  flex-shrink: 0;
}

.pay-range-scale__connector--red {
  background: var(--colors-background-bg-error-solid, #d92d20);
}

.pay-range-scale__connector--green {
  background: var(--colors-background-bg-success-solid, #079455);
}

.pay-range-scale__connector--blue {
  background: var(--component-colors-utility-blue-utility-blue-500, #2e90fa);
}

.pay-range-scale__step-icon {
  position: relative;
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 16px;
  background: var(--colors-background-bg-brand-primary, #f9f5ff);
  box-shadow: 0 0 0 4px rgba(158, 119, 237, 0.24);
  overflow: hidden;
}

.pay-range-scale__step-icon::before {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: var(--spacing-xl, 16px);
  border: 2px solid transparent;
}

.pay-range-scale__step-icon--red::before {
  background: var(--colors-background-bg-error-solid, #d92d20);
  border-color: var(--colors-background-bg-error-solid, #d92d20);
}

.pay-range-scale__step-icon--green::before {
  background: var(--colors-background-bg-success-solid, #079455);
  border-color: var(--colors-background-bg-success-solid, #079455);
}

.pay-range-scale__step-icon--blue::before {
  background: var(--component-colors-utility-blue-utility-blue-500, #2e90fa);
  border-color: var(--component-colors-utility-blue-utility-blue-500, #2e90fa);
}

.pay-range-scale__step-icon-dot {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 10px;
  height: 10px;
  transform: translate(-50%, -50%);
  border-radius: 50%;
  background: #fff;
}

.pay-range-scale__step-body {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xxs, 2px);
  padding-right: var(--spacing-xl, 16px);
  width: 100%;
}

.pay-range-scale__step-title {
  margin: 0;
  color: var(--colors-text-text-secondary-700, #344054);
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  word-break: break-word;
}

.pay-range-scale--disabled .pay-range-scale__step-title,
.pay-range-scale--disabled .pay-range-scale__supporting,
.pay-range-scale--disabled .pay-range-scale__input-value,
.pay-range-scale--disabled .pay-range-scale__input-unit {
  color: var(--colors-text-text-disabled, #667085);
}

.pay-range-scale__input {
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 44px;
  border-radius: var(--radius-md, 8px);
  border: 1px solid var(--stroke, #d0d5dd);
  background: var(--white, #fff);
  box-shadow: 0 1px 1px rgba(16, 24, 40, 0.05);
  box-sizing: border-box;
}

.pay-range-scale--vertical .pay-range-scale__input {
  align-items: flex-start;
}

.pay-range-scale__input--focused {
  border-color: var(--colors-border-border-brand, #d6bbfb);
  box-shadow:
    0 1px 2px rgba(16, 24, 40, 0.05),
    0 0 0 4px rgba(158, 119, 237, 0.24);
}

.pay-range-scale__input--changed {
  border-color: var(--colors-border-border-brand, #d6bbfb);
  background: var(--colors-background-bg-brand-primary, #f9f5ff);
  box-shadow: 0 1px 1px rgba(16, 24, 40, 0.05);
}

.pay-range-scale__input--error {
  border-color: var(--colors-border-border-error, #fda29b);
}

.pay-range-scale__input--disabled {
  border-color: var(--colors-border-border-disabled, #d0d5dd);
  background: var(--colors-background-bg-disabled, #f2f4f7);
  box-shadow: none;
}

.pay-range-scale__input-value {
  flex: 1 0 0;
  min-width: 0;
  padding: 10px 0 10px 14px;
  color: var(--colors-text-text-tertiary-600, #475467);
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  word-break: break-word;
}

.pay-range-scale--horizontal .pay-range-scale__input-value {
  padding-top: 0;
  padding-bottom: 0;
}

.pay-range-scale__input-unit {
  flex-shrink: 0;
  padding: 10px 14px;
  color: var(--headings-or-titles, #344054);
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  white-space: nowrap;
}

.pay-range-scale--horizontal .pay-range-scale__input-unit {
  padding-top: 0;
  padding-bottom: 0;
}

.pay-range-scale__supporting {
  margin: 0;
  color: var(--colors-text-text-tertiary-600, #475467);
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  word-break: break-word;
}

.pay-range-scale__supporting--min {
  padding: 3px 0 0 14px;
}

.pay-range-scale__supporting--wrap {
  width: 100%;
}

.pay-range-scale__supporting--vertical {
  padding: 3px 14px;
}

.pay-range-scale__error {
  margin: 0;
  padding: 3px 0 0 14px;
  color: var(--colors-text-text-error-primary-600, #d92d20);
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  word-break: break-word;
}

.pay-range-scale__error--vertical {
  padding: 3px 14px 0;
}

.pay-range-scale__steps--vertical {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.pay-range-scale__vertical-row {
  display: flex;
  gap: var(--spacing-xl, 16px);
  align-items: flex-start;
  width: 100%;
}

.pay-range-scale__connector-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-xs, 4px);
  align-self: stretch;
  padding-bottom: var(--spacing-xs, 4px);
}

.pay-range-scale__vertical-row:first-child .pay-range-scale__connector-wrap {
  padding-top: 4px;
}

.pay-range-scale__vertical-content {
  display: flex;
  flex: 1 0 0;
  flex-direction: column;
  gap: var(--spacing-xxs, 2px);
  min-width: 0;
}

.pay-range-scale__vertical-content--max {
  padding-bottom: 15px;
}

.pay-range-scale__vertical-content--milestone {
  padding: var(--spacing-xs, 4px) 0 var(--spacing-4xl, 32px);
}

.pay-range-scale__vertical-content--min-group {
  gap: 17px;
  padding: var(--spacing-xs, 4px) 0 var(--spacing-4xl, 32px);
}

.pay-range-scale__milestone-block {
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 100%;
}

.pay-range-scale__min-block {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xxs, 2px);
  padding: var(--spacing-xs, 4px) 0 15px;
  width: 100%;
}
</style>
